import { useState, useEffect } from 'react';
import { formatPropLockTime } from '@/utils/propBasedDailyReveal';

type CountdownRevealSectionProps = {
  nextRevealAt: string | null;
  isPropBased?: boolean;
  /**
   * The open time has passed but the day has not flipped to 'open' yet — show that it is
   * opening rather than a timer frozen at 00:00:00 (DKPLAT-882).
   */
  isOpening?: boolean;
};

function formatCountdown(ms: number): string {
  if (ms <= 0) return '00:00:00';

  const totalSeconds = Math.floor(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [hours, minutes, seconds].map(n => String(n).padStart(2, '0')).join(':');
}

export default function CountdownRevealSection({
  nextRevealAt,
  isPropBased = false,
  isOpening = false,
}: CountdownRevealSectionProps) {
  const [display, setDisplay] = useState('--:--:--');
  // Names the day the countdown is for, so a reveal opening tomorrow is never read as today's.
  const opensLabel = isOpening ? null : formatPropLockTime(nextRevealAt);

  useEffect(() => {
    if (!nextRevealAt) {
      setDisplay('--:--:--');
      return;
    }

    const target = new Date(nextRevealAt).getTime();
    if (!Number.isFinite(target)) {
      setDisplay('--:--:--');
      return;
    }

    const update = () => {
      setDisplay(formatCountdown(target - Date.now()));
    };

    update();
    const timer = setInterval(update, 1000);
    return () => clearInterval(timer);
  }, [nextRevealAt]);

  return (
    <section className="mb-6">
      <h2 className="daily-reveal-heading mb-1 text-center text-[14px] uppercase tracking-[0.42px]">
        {isPropBased ? 'Next Props Reveal' : 'Next Reveal'}
      </h2>
      {opensLabel && (
        <p
          className="mb-3 text-center text-[#8A8A8A]"
          style={{ fontFamily: 'Inter, sans-serif', fontWeight: 500, fontSize: '12px' }}
        >
          Opens {opensLabel}
        </p>
      )}

      <div
        className="relative mx-auto flex h-[110px] w-full items-center justify-between overflow-hidden rounded-[10px] px-4"
        style={{
          background: '#121212',
          border: '2px solid #4a4a4a',
        }}
      >
        <div className="flex flex-1 flex-col">
          <p
            className="uppercase"
            style={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: '11px',
              color: '#8a8a8a',
              letterSpacing: '0.3px',
            }}
          >
            Next available reveal
          </p>
          <p
            className="mt-1 font-bold tabular-nums"
            style={{
              fontFamily: "'Druk Text Wide', 'Arial Black', sans-serif",
              fontSize: '28px',
              fontWeight: 700,
              color: '#ffffff',
              letterSpacing: '0.5px',
            }}
          >
            {isOpening ? 'Opening…' : display}
          </p>
        </div>

        <img
          src="/DK-box.svg"
          alt=""
          aria-hidden
          className="h-[90px] w-[90px] shrink-0 object-contain opacity-40"
          style={{ filter: 'grayscale(0.3) brightness(0.7)' }}
        />
      </div>
    </section>
  );
}
